import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { ISocialsBlockData } from '../../types/content-block-data';
import { escapeHTML } from '../../lib/page-builder-lib';
import pageBuilderStore from '../../stores/pageBuilderStore';
import blocksManager from '../../stores/blocks-manager';
import serializationManager from '../../stores/serialization-manager';

function objectToInlineStyle(styles: Record<string, string | number | undefined>): string {
  return Object.entries(styles)
    .filter(([_, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}:${v}`)
    .join(';');
}

/**
 * Converts a SocialsBlock (IContentBlock) to an HTML string.
 * Renders as a <div> containing social icon links.
 *
 * @param block - The IContentBlock (should be blockType "socials")
 * @returns HTML string (e.g., <div class="socials-block"><a ...>...</a></div>)
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromSocialsBlockToHTML(block: IContentBlock): string {
  if (block.blockType !== "socials") return "";

  const isEmail = pageBuilderStore.getIsEmail();
  const data = block.blockData as ISocialsBlockData;
  const selector = block.selector || "socials-block";

  const { styles } = serializationManager.fromSocialsBlockToCssContent(data, block.selector);
  const inlineStyle = objectToInlineStyle(styles);

  const socialsHTML = (data.socials ?? []).map(social => {
    // Lowercase type for file name, fallback to "?" if missing
    const type = social.type.toLowerCase();
    // NOTE: You could optionally map X/twitter to "x" for file name
    const fileMap: Record<string, string> = { twitter: "x" }; // Add more if needed
    const imgName = fileMap[type] || type;
    // Only output an <img> if the PNG is expected to exist (for now: facebook, instagram, linkedin, x, pinterest)
    return `
      <a
        href="${escapeHTML(social.url)}"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="${escapeHTML(social.label || social.type)}"
        class="icon-link icon-${escapeHTML(type)}"
        style="display:inline-block; margin-right:8px;"
      >
        <img
          src="/img/${imgName}.png"
          alt="${escapeHTML(social.label || social.type)}"
          width="${data.iconSize}"
          height="${data.iconSize}"
          style="display:inline-block;vertical-align:middle; ${data.iconShape === "circle" ? "border-radius: 50%;" : "border-radius: 4px;"}"
        />
      </a>
    `.trim();
  }).join('');
  return isEmail 
    ? `<tr><td class="${selector}" style="${inlineStyle}">${socialsHTML}</td></tr>`
    : `<div class="${selector}" style="${inlineStyle}">${socialsHTML}</div>`;
}
/**
 * Serializes a SocialsBlock's data for storage or export.
 * Omits UI-only fields.
 *
 * @param block - The IContentBlock (should be blockType "socials")
 * @returns A JSON-serializable object
 */
export function fromSocialsBlockToJSON(block: IContentBlock): object {
  if (block.blockType !== "socials") return {};
  const data = block.blockData as ISocialsBlockData;
  return {
    contentBlockId: block.contentBlockId,
    blockType: block.blockType,
    selector: block.selector,
    blockData: {
      socials: (data.socials ?? []).map(social => ({
        type: social.type,
        url: social.url,
        // Add these if you allow editing later:
        label: social.label,
        color: social.color,
      })),
      iconShape: data.iconShape,
      iconSize: data.iconSize,
      alignment: data.alignment,
      spacing: data.spacing,
      // Add more blockData fields here if needed
    },
  };
}

/**
 * Reconstructs a SocialsBlock from JSON data.
 *
 * @param json - The JSON object to parse
 * @returns An IContentBlock instance (for use in your store/UI)
 */
export function fromJSONToSocialsBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
  // Provide safe defaults if fields are missing (for backward/forward compatibility)
  const blockData = json.blockData || {};
    
  const newDesignBlockRef = createRef<HTMLDivElement>();
  const newContentBlockRef = createRef<HTMLElement>(); // generic for now

  const block: IDesignBlock = {
    designBlockId: crypto.randomUUID(), // always generate fresh
    designBlockRef: newDesignBlockRef,
    contentBlock: {
      contentBlockId: json.contentBlockId,
      blockType: json.blockType,
      contentBlockRef: newContentBlockRef,
      selector: json.selector, // preserve selector if present
      blockData: {
        socials: (blockData.socials ?? []).map((social: any) => ({
          type: social.type,
          url: social.url,
          label: social.label,
          color: social.color,
        })),
        iconShape: blockData.iconShape ?? "circle",
        iconSize: blockData.iconSize ?? 32,
        alignment: blockData.alignment ?? "left",
        spacing: blockData.spacing ?? 8,
        // Add any additional fields with sensible defaults
      } as ISocialsBlockData,
    } as IContentBlock,
  };

  blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: context,
	});

  return block;
}
